import { DefaultError, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNotifications } from "hooks/notifications"
import { Creators } from "model/apiWrapper"
import { useMemo } from "react"
import { useDispatch } from "react-redux"
import { FetchThreadsResponse } from "../types/thread"
import { getThreadsQueryKey } from "../utils/threads-cache"

interface DeleteThreadPayload {
  accountAlias: string
  threadId: string
}

export function useDeleteThread() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()

  const { mutateAsync, isPending } = useMutation<unknown, DefaultError, DeleteThreadPayload>({
    mutationFn: ({ accountAlias, threadId }) => {
      return new Promise((resolve, reject) => {
        dispatch(Creators.delete(`/api/${accountAlias}/ai/chat/threads/${threadId}`, undefined, resolve, reject))
      })
    },
    onSuccess: (_, { accountAlias, threadId }) => {
      queryClient.setQueryData(getThreadsQueryKey(accountAlias), (old: FetchThreadsResponse) => {
        if (!old?.threads) {
          return old
        }
        return {
          threads: old.threads.filter((thread) => thread.threadId !== threadId),
        }
      })
    },
    onError: () => {
      notifs.error("An unexpected error occurred while deleting the thread.")
    },
  })

  return useMemo(() => ({
    deleteThread: mutateAsync,
    isDeletingThread: isPending,
  }), [mutateAsync, isPending])
}
